import { createContext, useContext, useEffect, useRef } from 'react'

export const DEFAULT_WS_SESSION_CONTEXT_VALUE = {
  wsConnected: false,
  subscribeConversation: () => {},
  unsubscribeConversation: () => {},
  // Track B2 (pagination-and-ws-tenant-wide) — tenant-wide conversation_list
  // topic, same safe-no-op default as the per-thread pair above.
  subscribeConversationList: () => {},
  unsubscribeConversationList: () => {},
  subscribe: () => () => {},
}

// Plain-JS context module (no JSX) so this can export the context object
// plus the two consumer hooks below without tripping
// react-refresh/only-export-components — the Provider component itself
// lives in WsSessionContext.jsx, the only export from that file.
export const WsSessionContext = createContext(DEFAULT_WS_SESSION_CONTEXT_VALUE)

export function useWsSession() {
  return useContext(WsSessionContext)
}

// Subscribes `handler` to WS events of `type` for the lifetime of the
// calling component. `handler` doesn't need to be memoized by the caller —
// it's kept fresh via a ref (updated in an effect, not during render, per
// react-hooks/refs), so the subscribe effect only re-runs when `type` (or
// the context's `subscribe` identity) changes, not on every render.
export function useWsEvent(type, handler) {
  const { subscribe } = useWsSession()
  const handlerRef = useRef(handler)
  useEffect(() => {
    handlerRef.current = handler
  })
  useEffect(() => subscribe(type, (event) => handlerRef.current?.(event)), [subscribe, type])
}
